import { ASSETS, PEOPLE, ROUTES } from '../config/site';
import { useLocale } from '../context/LocaleContext';
import TextLoop from '../components/reactbits/TextLoop/TextLoop';
import { FAQSection, FinalCTA, DossierPreview, MatchPreview, MediaFeature } from '../components/shared/PageSections';
import { SparkIcon } from '../components/ui/Icons';
import { ButtonLink, Eyebrow, TextLink } from '../components/ui/Primitives';

function HomeHero({ content, common }) {
  return (
    <section className="home-hero" aria-labelledby="home-title">
      <div className="home-hero__glow" aria-hidden="true" />
      <div className="home-hero__beam" aria-hidden="true" />
      <div className="home-hero__inner">
        <Eyebrow>{content.eyebrow}</Eyebrow>
        <div className="home-hero__headline-wrap">
          <h1 id="home-title">{content.title}</h1>
        </div>
        <p>{content.subtitle}</p>
        <div className="home-hero__actions">
          <ButtonLink to={ROUTES.schools}>{content.cta}</ButtonLink>
          <TextLink to="#product" arrow="down">{common.actions.discover}</TextLink>
        </div>
        <div className="home-hero__proof">
          <div>{PEOPLE.slice(0, 5).map((person) => <img key={person.name} src={person.src} alt="" />)}</div>
          <span>{content.proof}</span>
        </div>
      </div>
      <a className="scroll-indicator" href="#stories" aria-label={common.aria.scrollToContent}><span>{common.labels.scroll}</span><i /></a>
    </section>
  );
}

function StoryDeck({ content }) {
  return (
    <section id="stories" className="story-deck" aria-label={content.ariaLabel}>
      <div className="story-deck__track">
        {PEOPLE.map((person, index) => (
          <article key={person.name} className="story-card" style={{ '--index': index }}>
            <img src={person.src} alt={`${person.name}, ${person.company}`} loading={index > 2 ? 'lazy' : 'eager'} />
            <small>{content.cardLabel}</small>
          </article>
        ))}
      </div>
    </section>
  );
}

function TalentPoolPreview({ content, locale }) {
  return (
    <div className="talent-preview">
      <div className="talent-preview__halo" aria-hidden="true" />
      <img className="talent-preview__phone" src={ASSETS.phone} alt="YourApplicant" />
      {locale === 'de' ? (
        <img className="notification-card notification-card--asset" src={ASSETS.notification} alt={content.notification} />
      ) : (
        <div className="notification-card glass-card">
          <img src={ASSETS.logo} alt="" />
          <div><small>YourApplicant</small><strong>{content.notification}</strong><span>{content.description}</span></div>
        </div>
      )}
    </div>
  );
}

function JourneyIcon({ index }) {
  const icons = [
    <><circle cx="12" cy="8" r="3" /><path d="M6.5 19c.7-3 2.5-4.5 5.5-4.5S16.8 16 17.5 19" /></>,
    <><circle cx="10.5" cy="10.5" r="5" /><path d="m14.2 14.2 4.3 4.3" /></>,
    <><path d="M12 3.5c.6 4.1 1.9 5.4 6 6-4.1.6-5.4 1.9-6 6-.6-4.1-1.9-5.4-6-6 4.1-.6 5.4-1.9 6-6Z" /><path d="M18.5 15.5c.2 1.7.8 2.3 2.5 2.5-1.7.2-2.3.8-2.5 2.5-.2-1.7-.8-2.3-2.5-2.5 1.7-.2 2.3-.8 2.5-2.5Z" /></>,
    <><path d="M7 3.5h7l3 3V20H7Z" /><path d="M14 3.5V7h3M9.5 11h5M9.5 14h5M9.5 17h3" /></>,
    <><path d="M6 8h11M14 5l3 3-3 3M18 16H7M10 13l-3 3 3 3" /></>,
    <><ellipse cx="12" cy="6" rx="6" ry="2.5" /><path d="M6 6v6c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5V6M6 12v6c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5v-6" /></>,
  ];

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{icons[index]}</g>
    </svg>
  );
}

function Journey({ content }) {
  const middle = Math.ceil(content.pills.length / 2);
  const words = content.quote.split(' ');
  const renderPills = (pills, offset = 0) => pills.map((pill, index) => {
    const itemIndex = index + offset;
    return (
      <span className={`journey-pill journey-pill--tone-${itemIndex + 1}`} key={pill} style={{ '--stagger': index }}>
        <i><JourneyIcon index={itemIndex} /></i>
        {pill}
      </span>
    );
  });

  return (
    <section className="journey-section" aria-labelledby="journey-title">
      <div className="journey-section__ambient" aria-hidden="true" />
      <div className="journey-pills journey-pills--left" data-reveal="stagger" aria-hidden="true">
        {renderPills(content.pills.slice(0, middle))}
      </div>
      <div className="journey-section__copy">
        <Eyebrow>{content.eyebrow}</Eyebrow>
        <p id="journey-title" aria-label={content.quote} data-reveal="sentence">
          {words.map((word, index) => (
            <span aria-hidden="true" key={`${word}-${index}`} style={{ '--word-index': index }}>{word}{index < words.length - 1 ? ' ' : ''}</span>
          ))}
        </p>
      </div>
      <div className="journey-pills journey-pills--right" data-reveal="stagger" aria-hidden="true">
        {renderPills(content.pills.slice(middle), middle)}
      </div>
    </section>
  );
}

function CoachSection({ content, questions }) {
  return (
    <section className="coach-section">
      <div className="coach-section__copy" data-reveal="clip">
        <Eyebrow tone="light">{content.eyebrow}</Eyebrow>
        <h2>{content.title} <span>{content.titleAccent}</span></h2>
        <p>{content.description}</p>
        <ButtonLink to={ROUTES.coach} variant="light">{content.cta}</ButtonLink>
      </div>
      <div className="coach-console" data-reveal="scale" aria-label="Coach Anna">
        <div className="coach-console__header">
          <span className="coach-console__mark"><SparkIcon /></span>
          <div><strong>Coach Anna</strong><small><i /> Online</small></div>
          <span className="coach-console__step">01 / 03</span>
        </div>
        <div className="coach-console__conversation">
          <div className="coach-message coach-message--anna">
            <span className="coach-message__spark"><SparkIcon /></span>
            <p>{content.description}</p>
          </div>
          <div className="coach-console__questions">
            {questions.slice(0, 3).map((question, index) => (
              <span key={question} style={{ '--question-index': index }}>{question}</span>
            ))}
          </div>
          <div className="coach-console__typing" aria-hidden="true"><i /><i /><i /></div>
        </div>
        <div className="coach-console__footer" aria-hidden="true">
          <span />
          <i><SparkIcon /></i>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const { t, locale } = useLocale();
  const home = t.home;
  const dossierLabels = {
    kicker: home.dossier.eyebrow,
    traits: home.journey.pills.slice(0, 3).join(' · '),
    stats: [
      { label: home.journey.pills[0], value: '92%' },
      { label: home.journey.pills[1], value: '3×' },
      { label: home.journey.pills[4], value: '6' },
    ],
  };

  return (
    <main id="main-content">
      <HomeHero content={home.hero} common={t.common} />
      <StoryDeck content={home.stories} />

      <div className="home-text-loop" aria-hidden="true">
        <TextLoop
          text={home.ribbon}
          shape="wave"
          path="M 70 260 Q 205 165 340 260 T 610 260 T 880 260 T 1130 260"
          speed={52}
          direction="forward"
          separator="✦"
          curviness={58}
          fontSize={38}
          fontWeight={760}
          letterSpacing={0.8}
          uppercase
          color="#fff9f4"
          ribbon
          ribbonColor="#f4682f"
          ribbonWidth={96}
          pauseOnHover
        />
      </div>

      <div id="product">
        <MediaFeature
          eyebrow={home.dossier.eyebrow}
          title={`${home.dossier.title} ${home.dossier.titleAccent}`}
          text={home.dossier.description}
          buttonLabel={home.dossier.cta}
        >
          <DossierPreview labels={dossierLabels} />
        </MediaFeature>

        <MediaFeature
          reverse
          eyebrow={home.talentPool.eyebrow}
          title={`${home.talentPool.title} ${home.talentPool.titleAccent}`}
          text={home.talentPool.description}
          buttonLabel={home.talentPool.cta}
        >
          <TalentPoolPreview content={home.talentPool} locale={locale} />
        </MediaFeature>

        <MediaFeature
          eyebrow={home.matches.eyebrow}
          title={`${home.matches.title} ${home.matches.titleAccent}`}
          text={home.matches.description}
          buttonLabel={t.common.actions.exploreJobs}
        >
          <MatchPreview labels={{
            kicker: home.matches.companyLine,
            title: home.matches.role,
            apply: home.matches.apply,
            match: home.matches.strength,
            why: home.matches.reasonTitle,
            text: home.matches.reason,
          }} />
        </MediaFeature>
      </div>

      <Journey content={home.journey} />
      <CoachSection content={home.coach} questions={home.faq.items.map((item) => item.question)} />
      <FAQSection eyebrow={home.faq.eyebrow} title={home.faq.title} items={home.faq.items} />
      <FinalCTA eyebrow={home.closing.eyebrow} title={home.closing.title} text={home.closing.description} buttonLabel={home.closing.cta} />
    </main>
  );
}
